import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api } from '../../services/api'

// Phase 3 admin intersections viewer at /admin/initiatives/:slug/intersections.
//
// Admin-only — rendered behind AdminProtectedRoute (see App.jsx); the backend
// also 403s non-admins. Two tabs, tab state synced to the URL (?tab=concept |
// ?tab=person):
//
//   By concept — catalog entries ranked by distinct contributor count, with
//                expandable contributor lists (response chips link to the
//                initiative detail page, matching SourceProvenance).
//   By person  — typeahead member picker, then a ranked list of other members
//                by knowledge overlap score.

interface ApiUser {
  id: number
  full_name: string
  email: string
  company: string | null
}

interface InitiativeRef {
  id: number
  slug: string
  name: string
}

interface ConceptContributor {
  user: ApiUser
  response_ids: number[]
}

interface ConceptRow {
  catalog_entry: {
    id: number
    title: string
    entry_type: string
    summary: string | null
  }
  contributor_count: number
  latest_source_at: string | null
  contributors: ConceptContributor[]
}

interface PersonRow {
  user: ApiUser
  shared_entries: number
  shared_dimensions: number
  score: number
  sample_entry_titles: string[]
}

type TabKey = 'concept' | 'person'

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('')
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export default function Intersections() {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const tab: TabKey = searchParams.get('tab') === 'person' ? 'person' : 'concept'

  const setTab = useCallback(
    (next: TabKey) => {
      const params = new URLSearchParams(searchParams)
      params.set('tab', next)
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const [initiative, setInitiative] = useState<InitiativeRef | null>(null)

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <Link to={`/admin/initiatives/${slug}/queue`} className="text-sm text-slate-500 hover:text-slate-700">
          ← Back to queue
        </Link>
        <div className="mt-2 text-xs uppercase tracking-wide text-slate-500">
          {initiative?.name || 'Initiative'}
        </div>
        <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Intersections</h2>
        <p className="mt-1 text-sm text-slate-500">
          Where knowledge in this initiative overlaps — by shared concept and by person.
        </p>
      </div>

      <div className="flex gap-1.5 border-b border-slate-200 pb-2" role="tablist" aria-label="Intersection views">
        {([
          { value: 'concept', label: 'By concept' },
          { value: 'person', label: 'By person' },
        ] as { value: TabKey; label: string }[]).map(({ value, label }) => {
          const isActive = tab === value
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setTab(value)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                isActive
                  ? 'bg-accent-600 text-white shadow-sm'
                  : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {label}
            </button>
          )
        })}
      </div>

      {tab === 'concept' ? (
        <ByConceptTab slug={slug} onInitiative={setInitiative} />
      ) : (
        <ByPersonTab slug={slug} onInitiative={setInitiative} />
      )}
    </div>
  )
}

// ---- By concept ----------------------------------------------------------

function ByConceptTab({
  slug,
  onInitiative,
}: {
  slug?: string
  onInitiative: (i: InitiativeRef) => void
}) {
  const [rows, setRows] = useState<ConceptRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get(`/api/v1/admin/initiatives/${slug}/intersections/by_concept`)
      .then((data: { initiative: InitiativeRef; rows: ConceptRow[] }) => {
        if (cancelled) return
        onInitiative(data.initiative)
        setRows(data.rows || [])
      })
      .catch((e: any) => !cancelled && setError(e?.message || 'Failed to load concepts'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [slug, onInitiative])

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  if (loading) return <p className="text-sm text-slate-500">Loading concepts…</p>
  if (error) return <ErrorBox message={error} />
  if (rows.length === 0)
    return <EmptyBox text="No cataloged knowledge with contributors yet for this initiative." />

  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        const id = row.catalog_entry.id
        const isOpen = expanded.has(id)
        return (
          <li key={id} className="rounded-lg border border-slate-200 bg-white">
            <button
              type="button"
              onClick={() => toggle(id)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-slate-900">
                    {row.catalog_entry.title}
                  </span>
                  <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                    {row.catalog_entry.entry_type}
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  Latest source {formatDate(row.latest_source_at)}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="rounded-full bg-accent-50 px-2.5 py-1 text-xs font-medium text-accent-700">
                  {row.contributor_count}{' '}
                  {row.contributor_count === 1 ? 'contributor' : 'contributors'}
                </span>
                <span className="text-slate-400">{isOpen ? '▾' : '▸'}</span>
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-slate-100 px-4 py-3">
                <ul className="space-y-2">
                  {row.contributors.map((c) => (
                    <li key={c.user.id} className="flex items-start justify-between gap-3">
                      <div className="text-sm text-slate-700">
                        {c.user.full_name}
                        {c.user.company && (
                          <span className="text-slate-400"> · {c.user.company}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap justify-end gap-1">
                        {c.response_ids.length === 0 ? (
                          <span className="text-xs text-slate-400">no responses</span>
                        ) : (
                          // One chip per contributor summarizing how many
                          // responses they fed into this entry, linking to the
                          // initiative (slug-keyed). Per-response deep-linking is
                          // a v2 item — so we show a count, not raw Response ids.
                          <Link
                            to={`/initiatives/${slug}`}
                            className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200"
                            title="View initiative"
                          >
                            {c.response_ids.length} {c.response_ids.length === 1 ? 'response' : 'responses'}
                          </Link>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

// ---- By person -----------------------------------------------------------

function ByPersonTab({
  slug,
  onInitiative,
}: {
  slug?: string
  onInitiative: (i: InitiativeRef) => void
}) {
  const [members, setMembers] = useState<ApiUser[]>([])
  const [query, setQuery] = useState('')
  const [focal, setFocal] = useState<ApiUser | null>(null)

  const [rows, setRows] = useState<PersonRow[]>([])
  const [rowsLoading, setRowsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [initiativeId, setInitiativeId] = useState<number | null>(null)

  // Load the member list once for the typeahead.
  useEffect(() => {
    if (!slug) return
    let cancelled = false
    api
      .get(`/api/v1/admin/initiatives/${slug}/intersections/members`)
      .then((data: { members: ApiUser[] }) => {
        if (!cancelled) setMembers(data.members || [])
      })
      .catch((e: any) => !cancelled && setError(e?.message || 'Failed to load members'))
    return () => {
      cancelled = true
    }
  }, [slug])

  // Fetch overlap rows when a focal member is picked.
  useEffect(() => {
    if (!slug || !focal) {
      setRows([])
      return
    }
    let cancelled = false
    setRowsLoading(true)
    setError(null)
    api
      .get(`/api/v1/admin/initiatives/${slug}/intersections/by_person/${focal.id}`)
      .then((data: { initiative: InitiativeRef; rows: PersonRow[] }) => {
        if (cancelled) return
        onInitiative(data.initiative)
        setInitiativeId(data.initiative?.id ?? null)
        setRows(data.rows || [])
      })
      .catch((e: any) => !cancelled && setError(e?.message || 'Failed to load overlaps'))
      .finally(() => !cancelled && setRowsLoading(false))
    return () => {
      cancelled = true
    }
  }, [slug, focal, onInitiative])

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    return members
      .filter(
        (m) =>
          m.full_name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
      )
      .slice(0, 8)
  }, [query, members])

  return (
    <div className="grid gap-6 md:grid-cols-[16rem_1fr]">
      {/* Left: typeahead member picker */}
      <div>
        <label htmlFor="member-search" className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Teammate
        </label>
        <input
          id="member-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search members…"
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
        />
        {matches.length > 0 && (
          <ul className="mt-2 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white">
            {matches.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => {
                    setFocal(m)
                    setQuery('')
                  }}
                  className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-slate-50"
                >
                  <Avatar name={m.full_name} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-slate-800">{m.full_name}</span>
                    {m.company && <span className="block truncate text-xs text-slate-400">{m.company}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {focal && (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-accent-100 bg-accent-50 px-3 py-2">
            <Avatar name={focal.full_name} />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-slate-800">{focal.full_name}</span>
              {focal.company && <span className="block truncate text-xs text-slate-500">{focal.company}</span>}
            </span>
          </div>
        )}
      </div>

      {/* Right: ranked overlap list */}
      <div>
        {error && <ErrorBox message={error} />}
        {!focal && !error && (
          <EmptyBox text="Pick a teammate to see who they share knowledge with." />
        )}
        {focal && rowsLoading && <p className="text-sm text-slate-500">Loading overlaps…</p>}
        {focal && !rowsLoading && !error && rows.length === 0 && (
          <EmptyBox text="No knowledge overlap found with other members yet." />
        )}
        {focal && !rowsLoading && rows.length > 0 && (
          <ul className="space-y-2">
            {rows.map((row) => (
              <li
                key={row.user.id}
                className="rounded-lg border border-slate-200 bg-white px-4 py-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <Avatar name={row.user.full_name} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900">{row.user.full_name}</div>
                      {row.user.company && (
                        <div className="text-xs text-slate-400">{row.user.company}</div>
                      )}
                      <div className="mt-1 text-xs text-slate-500">
                        {row.shared_entries} shared {row.shared_entries === 1 ? 'entry' : 'entries'} ·{' '}
                        {row.shared_dimensions} shared{' '}
                        {row.shared_dimensions === 1 ? 'dimension' : 'dimensions'}
                      </div>
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-accent-600 px-2.5 py-1 text-xs font-semibold text-white">
                    {row.score}
                  </span>
                </div>
                {row.sample_entry_titles.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {row.sample_entry_titles.map((title) => (
                      <CatalogTitleLink key={title} title={title} initiativeId={initiativeId} />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

// Quiet link to the catalog, filtered to this initiative with the entry title
// as a search term, so the admin can dig into a shared concept. Matches the
// plan's /catalog?initiative_id=…&search=<title> shape (§4.1).
function CatalogTitleLink({ title, initiativeId }: { title: string; initiativeId: number | null }) {
  const params = new URLSearchParams()
  if (initiativeId != null) params.set('initiative_id', String(initiativeId))
  params.set('search', title)
  return (
    <Link
      to={`/catalog?${params.toString()}`}
      className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200"
    >
      {title}
    </Link>
  )
}

// ---- Shared bits ---------------------------------------------------------

function Avatar({ name }: { name: string }) {
  return (
    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
      {initials(name)}
    </span>
  )
}

function EmptyBox({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
      <p className="text-sm text-slate-500">{text}</p>
    </div>
  )
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
      {message}
    </div>
  )
}
